import { Button } from '@unifyapps/ui/components/Button';
import type { ComputedButtonAppearance } from '@unifyapps/defs/types/blocks';
import { clsx } from 'clsx';
import {
  getConditionalIcon,
  getConditionalIconNode,
} from '@unifyapps/carbon/utils/conditionalValues';
import { forwardRef, type SyntheticEvent } from 'react';
import { Tooltip as BaseTooltip } from '@unifyapps/ui/components/Tooltip';

type BaseButtonProps = {
  appearance?: ComputedButtonAppearance;
  className?: string;
  loading?: boolean;
  onClick?: (e: SyntheticEvent) => void;
  size?: ComputedButtonAppearance['size'];
  style?: React.CSSProperties;
  type?: 'default' | 'submit' | 'reset';
  attributesAndEventListeners?: Record<string, unknown>;
  value: string;
  tooltip?: { content?: string };
};

const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(function BaseButton(props, ref) {
  const {
    appearance,
    className,
    loading,
    onClick,
    size,
    style,
    type,
    attributesAndEventListeners,
    value,
    tooltip,
  } = props;

  const buttonEl = (
    <Button
      aria-disabled={appearance?.disabled}
      className={clsx(className, appearance?.className)}
      color={appearance?.color ?? 'brand'}
      disabled={appearance?.disabled}
      endDecoratorComponent={getConditionalIcon(appearance?.endDecorator)}
      endDecoratorNode={getConditionalIconNode(appearance?.endDecorator, size)}
      loading={appearance?.loading || loading}
      onClick={onClick}
      ref={ref}
      size={size}
      startDecoratorComponent={getConditionalIcon(appearance?.startDecorator)}
      startDecoratorNode={getConditionalIconNode(appearance?.startDecorator, size)}
      style={style}
      type={type === 'default' ? 'button' : type}
      value={value}
      variant={appearance?.variant ?? 'solid'}
      {...attributesAndEventListeners}
    >
      {`${value}`}
    </Button>
  );

  if (tooltip?.content) {
    return <BaseTooltip title={tooltip.content}>{buttonEl}</BaseTooltip>;
  }

  return buttonEl;
});

export default BaseButton;
